import type { Currency } from '@erp/shared';
import { Coins } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { Button } from '../../components/ui/index.js';
import { platformApi } from '../../lib/platformApi.js';

/**
 * Platform-admin control of each currency's decimal places (`exponent`) — the
 * single backend lever for money precision (drives rounding AND display
 * everywhere). The catalog is global, so an edit propagates to the platform DB
 * and every tenant's split-DB copy server-side. Example: IQD = 0 (whole dinars).
 */
export function PlatformCurrenciesPage(): JSX.Element {
  const { t } = useTranslation('platform');
  const [rows, setRows] = useState<Currency[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    try {
      const r = await platformApi().get<{ items: Currency[] }>('/currencies');
      setRows(r.data.items);
      setDrafts(Object.fromEntries(r.data.items.map((c) => [c.code, c.exponent])));
    } catch {
      toast.error(t('currencies.loadError', { defaultValue: 'Could not load currencies.' }));
    }
  };

  useEffect(() => { void load(); }, []);

  const save = async (c: Currency): Promise<void> => {
    const exponent = drafts[c.code];
    if (exponent == null || exponent === c.exponent) return;
    setSaving(c.code);
    try {
      const r = await platformApi().patch<Currency>(`/currencies/${c.code}`, { exponent });
      setRows((prev) => prev?.map((x) => (x.code === c.code ? r.data : x)) ?? prev);
      toast.success(t('currencies.saved', { defaultValue: '{{code}} now shows {{n}} decimals everywhere.', code: c.code, n: exponent }));
    } catch {
      toast.error(t('currencies.saveError', { defaultValue: 'Could not update {{code}}.', code: c.code }));
      setDrafts((d) => ({ ...d, [c.code]: c.exponent })); // revert the draft
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-warning/15 text-warning">
          <Coins className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('currencies.title', { defaultValue: 'Currencies' })}</h1>
          <p className="text-sm text-fg-muted">
            {t('currencies.subtitle', { defaultValue: 'Decimal places shown for each currency, applied everywhere (e.g. IQD = 0 for whole dinars).' })}
          </p>
        </div>
      </header>

      {!rows ? (
        <Card className="p-4"><Skeleton className="h-6 w-40" /><Skeleton className="mt-4 h-64 w-full" /></Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-start text-sm">
            <thead className="border-b border-border bg-bg-subtle text-fg-muted">
              <tr>
                <th className="px-4 py-2.5 text-start">{t('currencies.code', { defaultValue: 'Code' })}</th>
                <th className="px-4 py-2.5 text-start">{t('currencies.name', { defaultValue: 'Name' })}</th>
                <th className="px-4 py-2.5 text-start">{t('currencies.symbol', { defaultValue: 'Symbol' })}</th>
                <th className="px-4 py-2.5 text-start">{t('currencies.decimals', { defaultValue: 'Decimal places' })}</th>
                <th className="px-4 py-2.5 text-end">{t('currencies.preview', { defaultValue: 'Preview' })}</th>
                <th className="w-24 px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const draft = drafts[c.code] ?? c.exponent;
                const dirty = draft !== c.exponent;
                const preview = (1234.5678).toLocaleString('en-US', { minimumFractionDigits: draft, maximumFractionDigits: draft });
                return (
                  <tr key={c.code} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-2.5 font-mono font-semibold">{c.code}</td>
                    <td className="px-4 py-2.5">{c.name}</td>
                    <td className="px-4 py-2.5 text-fg-muted">{c.symbol}</td>
                    <td className="px-4 py-2.5">
                      <Input
                        type="number"
                        min={0}
                        max={4}
                        value={String(draft)}
                        onChange={(e) => {
                          const n = Math.max(0, Math.min(4, Math.floor(Number(e.target.value) || 0)));
                          setDrafts((d) => ({ ...d, [c.code]: n }));
                        }}
                        className="h-9 w-20 text-center font-mono"
                        aria-label={t('currencies.decimals', { defaultValue: 'Decimal places' })}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-end font-mono tabular-nums text-fg-muted">{c.symbol} {preview}</td>
                    <td className="px-4 py-2.5 text-end">
                      <Button
                        size="sm"
                        variant={dirty ? 'primary' : 'ghost'}
                        disabled={!dirty || saving === c.code}
                        loading={saving === c.code}
                        onClick={() => void save(c)}
                      >
                        {t('currencies.save', { defaultValue: 'Save' })}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
